// Location hierarchy powering AfriMarket's geo-first navigation and SEO.
// Structure:
//   COUNTRY { slug, name, flag, type: 'africa' | 'diaspora', cities[], areas? }
//   CITIES { slug, name, areas? }
//
// URL conventions:
//   /locations                                    → index of all countries
//   /near-me                                      → geolocation-driven discovery
//   /:countrySlug                                 → country hub  (e.g. /nigeria)
//   /:countrySlug/:citySlug                       → city hub     (e.g. /nigeria/lagos)
//   /:countrySlug/:citySlug/:areaSlug             → area hub     (e.g. /uk/london/peckham)
//
// Category overlay (handled in component, not data):
//   ?cat=fashion adds &category=Fashion%20%26%20Ankara to the listings filter

export const COUNTRIES = {
  nigeria: {
    name: "Nigeria",
    flag: "🇳🇬",
    type: "africa",
    apiCountry: "Nigeria",
    intro:
      "Nigeria — Africa's largest economy and the cultural powerhouse of the continent. From Lagos couture to Abuja jollof, Kano leatherwork to Port Harcourt's bustling markets, every state on AfriMarket brings something irreplaceable to the global African marketplace.",
    cities: [
      { slug: "lagos", name: "Lagos", apiCity: "Lagos",
        blurb: "Africa's fashion and tech capital. Buy from Lagos tailors, designers, beauty brands and food artisans shipping globally.",
        areas: [
          { slug: "ikeja", name: "Ikeja" },
          { slug: "lekki", name: "Lekki" },
          { slug: "victoria-island", name: "Victoria Island" },
          { slug: "ikoyi", name: "Ikoyi" },
          { slug: "yaba", name: "Yaba" },
          { slug: "surulere", name: "Surulere" },
        ],
      },
      { slug: "abuja", name: "Abuja (FCT)", apiCity: "Abuja",
        blurb: "Nigeria's capital. Polished, modern and home to top events, catering and lifestyle brands." },
      { slug: "kano", name: "Kano", apiCity: "Kano",
        blurb: "Centuries-old commercial hub. Leather, dyed fabric, kilishi and centuries of trade craftsmanship." },
      { slug: "rivers-port-harcourt", name: "Port Harcourt", apiCity: "Port Harcourt",
        blurb: "Rivers State's coastal capital — seafood, oil-and-gas adjacent businesses and Niger Delta crafts." },
      { slug: "oyo-ibadan", name: "Ibadan", apiCity: "Ibadan",
        blurb: "Oyo State's heart — adire fabric, amala, and the rich cultural soul of Yorubaland." },
      { slug: "enugu", name: "Enugu", apiCity: "Enugu",
        blurb: "Coal city. Igbo cultural produce, tailoring and a thriving service economy." },
      { slug: "anambra-onitsha", name: "Onitsha", apiCity: "Onitsha",
        blurb: "Anambra's commercial engine — the famous Onitsha main market reaches global buyers via AfriMarket." },
      { slug: "delta-warri", name: "Warri", apiCity: "Warri",
        blurb: "Delta State seafood, isi-ewu specialists, and growing fashion houses." },
      { slug: "edo-benin-city", name: "Benin City", apiCity: "Benin City",
        blurb: "Edo State — bronze craft heritage, fashion, and Benin's distinctive cuisine." },
      { slug: "kaduna", name: "Kaduna", apiCity: "Kaduna",
        blurb: "Northern Nigeria's industrial centre. Atelier-quality tailoring and northern delicacies." },
      { slug: "ogun-abeokuta", name: "Abeokuta", apiCity: "Abeokuta",
        blurb: "Ogun State's adire-dyeing capital — get authentic indigo and resist-dyed Yoruba textiles." },
      { slug: "imo-owerri", name: "Owerri", apiCity: "Owerri",
        blurb: "Imo State — Igbo cuisine specialists, fashion designers and event services." },
    ],
  },
  ghana: {
    name: "Ghana",
    flag: "🇬🇭",
    type: "africa",
    apiCountry: "Ghana",
    intro:
      "Ghana — birthplace of Kente cloth, world-class cocoa, and home to West Africa's most refined creative scene. AfriMarket connects you with Ghanaian artisans, tailors, caterers and entrepreneurs shipping globally.",
    cities: [
      { slug: "accra", name: "Accra", apiCity: "Accra",
        blurb: "Ghana's vibrant capital — Kente weavers, contemporary designers, beach-side caterers and a buzzing tech scene." },
      { slug: "kumasi", name: "Kumasi", apiCity: "Kumasi",
        blurb: "Ashanti Region capital. The traditional home of Kente weaving and Akan gold craftsmanship." },
      { slug: "tamale", name: "Tamale", apiCity: "Tamale",
        blurb: "Northern Region — shea butter cooperatives, smock weaving and dawadawa specialists." },
      { slug: "takoradi", name: "Takoradi", apiCity: "Takoradi",
        blurb: "Western Region coastal hub — fresh seafood, oil-and-gas businesses and Twin City crafts." },
      { slug: "cape-coast", name: "Cape Coast", apiCity: "Cape Coast",
        blurb: "Central Region — historic, cultural and home to growing fashion and tourism businesses." },
    ],
  },
  kenya: {
    name: "Kenya",
    flag: "🇰🇪",
    type: "africa",
    apiCountry: "Kenya",
    intro:
      "Kenya — East Africa's commercial powerhouse, home to world-class coffee, Maasai beadwork, kitenge fashion and a booming tech scene. AfriMarket Kenya connects buyers worldwide with Kenyan makers.",
    cities: [
      { slug: "nairobi", name: "Nairobi", apiCity: "Nairobi",
        blurb: "Kenya's capital — Maasai jewellery, kitenge fashion, specialty coffee and a vibrant restaurant scene." },
      { slug: "mombasa", name: "Mombasa", apiCity: "Mombasa",
        blurb: "Coastal capital — Swahili cuisine, kanga fabric, kahawa coffee and beach-side artisans." },
      { slug: "kisumu", name: "Kisumu", apiCity: "Kisumu",
        blurb: "Lake Victoria's commercial centre — fresh tilapia, Luo cultural crafts and growing fashion brands." },
      { slug: "nakuru", name: "Nakuru", apiCity: "Nakuru",
        blurb: "Rift Valley hub — agriculture, fashion and a fast-growing service economy." },
    ],
  },
  "south-africa": {
    name: "South Africa",
    flag: "🇿🇦",
    type: "africa",
    apiCountry: "South Africa",
    intro:
      "South Africa — the continent's most diverse marketplace. Shweshwe fashion, Cape Malay cuisine, contemporary art and luxury craftsmanship blend Pan-African heritage with global polish.",
    cities: [
      { slug: "johannesburg", name: "Johannesburg", apiCity: "Johannesburg",
        blurb: "Jozi — Africa's biggest commercial hub. Designer fashion, contemporary art, gourmet braai catering." },
      { slug: "cape-town", name: "Cape Town", apiCity: "Cape Town",
        blurb: "Mother City — Cape Malay cuisine, beadwork, contemporary design and world-class hospitality." },
      { slug: "durban", name: "Durban", apiCity: "Durban",
        blurb: "KZN's coastal hub — Zulu beadwork, bunny chow, surf culture and growing food brands." },
      { slug: "pretoria", name: "Pretoria", apiCity: "Pretoria",
        blurb: "Tshwane — diplomatic capital with refined fashion, fine dining and tech businesses." },
    ],
  },
  uganda: {
    name: "Uganda",
    flag: "🇺🇬",
    type: "africa",
    apiCountry: "Uganda",
    intro:
      "Uganda — the Pearl of Africa. From Kampala's bustling markets to Jinja's Nile-side artisans, Ugandan sellers on AfriMarket bring barkcloth, Buganda crafts, matoke specialists and modern fashion to global buyers.",
    cities: [
      { slug: "kampala", name: "Kampala", apiCity: "Kampala",
        blurb: "Uganda's capital — barkcloth fashion, Buganda crafts, matoke caterers and rolex-on-the-go specialists." },
      { slug: "entebbe", name: "Entebbe", apiCity: "Entebbe",
        blurb: "Lake Victoria hub — fresh fish, beach catering, weekend retreat businesses." },
      { slug: "jinja", name: "Jinja", apiCity: "Jinja",
        blurb: "Source of the Nile — adventure tourism, river-side cafes and artisans." },
    ],
  },
  cameroon: {
    name: "Cameroon",
    flag: "🇨🇲",
    type: "africa",
    apiCountry: "Cameroon",
    intro:
      "Cameroon — Africa in miniature. Bamileke beadwork, Bafut royal crafts, ndolé caterers and a vibrant bilingual fashion scene reach global buyers via AfriMarket.",
    cities: [
      { slug: "yaounde", name: "Yaoundé", apiCity: "Yaoundé",
        blurb: "Cameroon's capital — fashion, restaurants and a rich bilingual creative scene." },
      { slug: "douala", name: "Douala", apiCity: "Douala",
        blurb: "Commercial capital — sapeur fashion, ndolé restaurants and import-export businesses." },
      { slug: "bamenda", name: "Bamenda", apiCity: "Bamenda",
        blurb: "North-West regional hub — Bamileke beadwork, royal crafts and highland produce." },
    ],
  },
  tanzania: {
    name: "Tanzania",
    flag: "🇹🇿",
    type: "africa",
    apiCountry: "Tanzania",
    intro:
      "Tanzania — kanga fabric, Tinga Tinga art, Zanzibari spices and Swahili coastal cuisine. AfriMarket connects you with Tanzanian artisans and entrepreneurs across the country.",
    cities: [
      { slug: "dar-es-salaam", name: "Dar es Salaam", apiCity: "Dar es Salaam",
        blurb: "Tanzania's economic hub — kanga, Tinga Tinga art, Swahili cuisine and modern fashion." },
      { slug: "arusha", name: "Arusha", apiCity: "Arusha",
        blurb: "Safari capital — Maasai beadwork, gemstones, coffee and tour operators." },
      { slug: "zanzibar", name: "Zanzibar", apiCity: "Zanzibar",
        blurb: "Spice islands — clove, cardamom, Zanzibari cuisine and Stone Town crafts." },
    ],
  },
  ethiopia: {
    name: "Ethiopia",
    flag: "🇪🇹",
    type: "africa",
    apiCountry: "Ethiopia",
    intro:
      "Ethiopia — birthplace of coffee, home of habesha kemis, injera, berbere spice and an unbroken cultural heritage. AfriMarket Ethiopian sellers ship globally.",
    cities: [
      { slug: "addis-ababa", name: "Addis Ababa", apiCity: "Addis Ababa",
        blurb: "Ethiopia's capital — habesha kemis, specialty coffee, traditional jewellery and contemporary fashion." },
      { slug: "bahir-dar", name: "Bahir Dar", apiCity: "Bahir Dar",
        blurb: "Amhara region — Lake Tana crafts, traditional textiles and fish caterers." },
    ],
  },

  // ── Diaspora countries ────────────────────────────────────────────────────
  uk: {
    name: "United Kingdom",
    flag: "🇬🇧",
    type: "diaspora",
    apiCountry: "United Kingdom",
    intro:
      "The UK is home to one of the largest African diaspora communities in the world. From Peckham's Nigerian food scene to Birmingham's afro-Caribbean fashion, AfriMarket UK is where the diaspora finds home — and home finds the diaspora.",
    cities: [
      { slug: "london", name: "London", apiCity: "London",
        blurb: "The UK's African capital — Peckham, Brixton, Tottenham, Woolwich and beyond. Find a braider, caterer, tailor or designer in your borough.",
        areas: [
          { slug: "peckham", name: "Peckham" },
          { slug: "brixton", name: "Brixton" },
          { slug: "woolwich", name: "Woolwich" },
          { slug: "croydon", name: "Croydon" },
          { slug: "tottenham", name: "Tottenham" },
          { slug: "stratford", name: "Stratford" },
          { slug: "lewisham", name: "Lewisham" },
          { slug: "hackney", name: "Hackney" },
          { slug: "ilford", name: "Ilford" },
          { slug: "thornton-heath", name: "Thornton Heath" },
        ],
      },
      { slug: "birmingham", name: "Birmingham", apiCity: "Birmingham",
        blurb: "Britain's second city — vibrant African and Caribbean food, fashion and event services across the Midlands." },
      { slug: "manchester", name: "Manchester", apiCity: "Manchester",
        blurb: "The North-West's growing African hub — Moss Side caterers, Rusholme designers and event professionals." },
      { slug: "leeds", name: "Leeds", apiCity: "Leeds",
        blurb: "West Yorkshire — Chapeltown's African community, caterers, braiders and small businesses." },
      { slug: "liverpool", name: "Liverpool", apiCity: "Liverpool",
        blurb: "Merseyside's historic African community — caterers, hairstylists and cultural businesses." },
      { slug: "sheffield", name: "Sheffield", apiCity: "Sheffield",
        blurb: "South Yorkshire — Burngreave African food, family salons and event services." },
      { slug: "nottingham", name: "Nottingham", apiCity: "Nottingham",
        blurb: "East Midlands — Hyson Green's African community, caterers and creatives." },
      { slug: "leicester", name: "Leicester", apiCity: "Leicester",
        blurb: "England's most diverse city — African and Pan-African services across the East Midlands." },
      { slug: "bristol", name: "Bristol", apiCity: "Bristol",
        blurb: "West Country — St Pauls' African community, food businesses and stylists." },
      { slug: "coventry", name: "Coventry", apiCity: "Coventry",
        blurb: "West Midlands — Foleshill African caterers, hairdressers and event businesses." },
      { slug: "glasgow", name: "Glasgow", apiCity: "Glasgow",
        blurb: "Scotland's largest city — growing African food and beauty scene." },
      { slug: "edinburgh", name: "Edinburgh", apiCity: "Edinburgh",
        blurb: "Scotland's capital — African caterers, fashion designers and event professionals." },
    ],
  },
  usa: {
    name: "United States",
    flag: "🇺🇸",
    type: "diaspora",
    apiCountry: "United States",
    intro:
      "From Atlanta's Nigerian community to NYC's Senegalese stylists, Houston's Ghanaian caterers to DC's Ethiopian restaurants — AfriMarket USA connects the African diaspora across America.",
    cities: [
      { slug: "new-york", name: "New York", apiCity: "New York",
        blurb: "NYC — Harlem, the Bronx, Brooklyn. African and diaspora fashion, food, braiding and event services across the five boroughs." },
      { slug: "atlanta", name: "Atlanta", apiCity: "Atlanta",
        blurb: "ATL — the African diaspora capital of the South. Nigerian fashion, Ghanaian caterers, Pan-African events and beauty." },
      { slug: "houston", name: "Houston", apiCity: "Houston",
        blurb: "H-Town — one of America's largest Nigerian and Ghanaian communities. Caterers, designers, salons and tailors throughout the metro." },
      { slug: "washington-dc", name: "Washington DC", apiCity: "Washington",
        blurb: "DC metro — Ethiopian, Eritrean and West African businesses across U Street and Silver Spring." },
      { slug: "chicago", name: "Chicago", apiCity: "Chicago",
        blurb: "Chi-Town — African food, fashion and event professionals across the North, South and West sides." },
      { slug: "dallas", name: "Dallas", apiCity: "Dallas",
        blurb: "DFW — large Nigerian and Ghanaian communities with caterers, tailors and beauty pros." },
      { slug: "los-angeles", name: "Los Angeles", apiCity: "Los Angeles",
        blurb: "LA — Inglewood, Leimert Park and the broader African and Black-owned business scene." },
      { slug: "philadelphia", name: "Philadelphia", apiCity: "Philadelphia",
        blurb: "Philly — West and South Philly African community, food, fashion and culture." },
      { slug: "minneapolis", name: "Minneapolis", apiCity: "Minneapolis",
        blurb: "Minneapolis-St Paul — America's largest Somali and East African community." },
      { slug: "boston", name: "Boston", apiCity: "Boston",
        blurb: "Greater Boston — Mattapan, Dorchester African and Caribbean businesses." },
    ],
  },
  canada: {
    name: "Canada",
    flag: "🇨🇦",
    type: "diaspora",
    apiCountry: "Canada",
    intro:
      "Canada's African communities — from Toronto's Little Jamaica and Eglinton corridor to Calgary's growing Eritrean scene — find each other on AfriMarket.",
    cities: [
      { slug: "toronto", name: "Toronto", apiCity: "Toronto",
        blurb: "Canada's largest African diaspora hub — caterers, braiders, fashion designers and event services across the GTA." },
      { slug: "montreal", name: "Montreal", apiCity: "Montreal",
        blurb: "Québec — bilingual African community with French-speaking caterers, salons and creatives." },
      { slug: "calgary", name: "Calgary", apiCity: "Calgary",
        blurb: "Alberta — growing East African community with food, fashion and family services." },
      { slug: "vancouver", name: "Vancouver", apiCity: "Vancouver",
        blurb: "BC's Pan-African hub — caterers, designers, event professionals on the west coast." },
      { slug: "ottawa", name: "Ottawa", apiCity: "Ottawa",
        blurb: "Canada's capital — established African and East African community with restaurants and services." },
      { slug: "edmonton", name: "Edmonton", apiCity: "Edmonton",
        blurb: "Alberta's other African capital — growing food and fashion scene." },
    ],
  },
};

export const COUNTRY_LIST = Object.entries(COUNTRIES).map(([slug, c]) => ({
  slug,
  ...c,
}));

export function findCountry(slug) {
  return COUNTRIES[slug] ? { slug, ...COUNTRIES[slug] } : null;
}

export function findCity(countrySlug, citySlug) {
  const country = findCountry(countrySlug);
  if (!country) return null;
  const city = country.cities.find((c) => c.slug === citySlug);
  return city ? { ...city, country } : null;
}

export function findArea(countrySlug, citySlug, areaSlug) {
  const city = findCity(countrySlug, citySlug);
  if (!city || !city.areas) return null;
  const area = city.areas.find((a) => a.slug === areaSlug);
  return area ? { ...area, city, country: city.country } : null;
}
